import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { SharedModule } from '@/app/shared/shared.module';
import { DialogService } from 'primeng/dynamicdialog';
import { KeyValueModel, NoteTagsLookupModel, NoteViewModel } from '@/app/proxy/proxies';
import { TranslateService } from '@ngx-translate/core';
import { CropHealthNoteItemComponent } from '../crop-health-note-item/crop-health-note-item.component';
import { NoteFilterResponse, CropHealthNoteAllFilterModalComponent } from './crop-health-note-all-filter-modal/crop-health-note-all-filter-modal.component';
import { CropHealthService } from '../../../../services/crop-health.service';

@Component({
  selector: 'app-crop-health-note-all',
  imports: [SharedModule, CropHealthNoteItemComponent],
  templateUrl: './crop-health-note-all.component.html',
  styleUrl: './crop-health-note-all.component.less',
  providers: [DialogService],
})
export class CropHealthNoteAllComponent implements OnInit {
  @Input() loading = false;
  @Output() back = new EventEmitter();
  @Output() getAllNotes = new EventEmitter();
  @Output() viewNote = new EventEmitter();
  
  notes: NoteViewModel[] = [];
  noteTags: NoteTagsLookupModel[] = [];
  noteTypes: KeyValueModel[] = [];
  sortOptions: any[] = [
    {
      label: 'doktar_app.common.sort_by_newest',
      value: 'newest',
      selected: true,
    },
    {
      label: 'doktar_app.common.sort_by_oldest',
      value: 'oldest',
      selected: false,
    },
    {
      label: 'doktar_app.services.crop_monitoring.sort_by_note_title_az',
      value: 'az',
      selected: false,
    },
    {
      label: 'doktar_app.services.crop_monitoring.sort_by_note_title_za',
      value: 'za',
      selected: false,
    },
  ];

  filteredNotes: NoteViewModel[] = [];
  searchText: string;
  filterParameters: NoteFilterResponse;

  constructor(
    private dialogService: DialogService,
    private translateService: TranslateService,
    private cropHealthService: CropHealthService,
  ) {}

  ngOnInit(): void {
    this.cropHealthService.noteLookups$.subscribe(res => {
      this.noteTags = res.noteTags ?? [];
      this.noteTypes = res.noteTypes ?? [];
    });
    this.cropHealthService.notes$.subscribe(res => {
      this.notes = res;
      this.filteredNotes = [...this.notes];
    });
  }

  onSelectSortItem(sortItemIndex: number) {
    for (const sortItem of this.sortOptions) {
      sortItem.selected = false;
    }

    this.sortOptions[sortItemIndex].selected = true;
    switch (this.sortOptions[sortItemIndex].value) {
      case 'az': {
        this.filteredNotes = this.filteredNotes.sort((a, b) => (a.title ?? '').localeCompare(b.title ?? ''));
        break;
      }
      case 'za': {
        this.filteredNotes = this.filteredNotes.sort((a, b) => (b.title ?? '').localeCompare(a.title ?? ''));
        break;
      }
      case 'newest': {
        this.filteredNotes = this.filteredNotes.sort(
          (a, b) => (b.createdAt?.toMillis() ?? 0) - (a.createdAt?.toMillis() ?? 0)
        );
        break;
      }
      case 'oldest': {
        this.filteredNotes = this.filteredNotes.sort(
          (a, b) => (a.createdAt?.toMillis() ?? 0) - (b.createdAt?.toMillis() ?? 0)
        );
        break;
      }
    }
  }

  onOpenFilterModal() {
    const modalRef = this.dialogService.open(CropHealthNoteAllFilterModalComponent, {
      header: this.translateService.instant('doktar_app.common.filter'),
      modal: true,
      closable: true,
      width: '450px',
      inputValues: {
        notes: this.notes,
        noteTags: this.noteTags,
        filterParameters: this.filterParameters,
      },
    });

    modalRef.onClose.subscribe((res: any) => {
      for (const key of Object.keys(res ?? {})) {
        if (!res[key]) {
          delete res[key];
        }
      }

      this.filterParameters = res && Object.keys(res).length > 0 ? res : null;
      this.applyFilterParameters();
    });
  }

  applyFilterParameters() {
    if (!this.filterParameters) {
      this.filteredNotes = [...this.notes];

      const searchText = this.searchText?.trim().toLowerCase();
      if (searchText?.length >= 3) {
        this.onSearch();
      }
      return;
    }

    let filteredNotes = [...this.notes];
    if (this.filterParameters.year) {
      filteredNotes = filteredNotes.filter((note) => this.filterParameters.year === note.createdAt.toFormat('yyyy'));
    }
    if (this.filterParameters.noLabel) {
      filteredNotes = filteredNotes.filter((note) => note.noteTags?.length === 0);
    } else {
      if (this.filterParameters.labels?.length > 0) {
        filteredNotes = filteredNotes.filter((note) => {
          const noteTagLabels = note.noteTags.map((e) => e.name);
          if (this.filterParameters.labels.some((e: string) => noteTagLabels.includes(e))) {
            return true;
          }
          return false;
        });
      }
    }

    if (this.filterParameters.photosAttached) {
      filteredNotes = filteredNotes.filter((note) => note.fileList?.length > 0);
    } else {
      if (this.filterParameters.photosNotAttached) {
        filteredNotes = filteredNotes.filter((note) => note.fileList?.length === 0);
      }
    }

    if (this.filterParameters.descriptionAdded) {
      filteredNotes = filteredNotes.filter((note) => note.noteContent?.trim().length > 0);
    } else {
      if (this.filterParameters.descriptionNotAdded) {
        filteredNotes = filteredNotes.filter((note) => !(note.noteContent?.trim().length > 0));
      }
    }
    this.filteredNotes = filteredNotes;
  }

  onSearch() {
    const searchText = this.searchText?.trim().toLowerCase();

    if (!searchText || searchText?.length < 3) {
      this.filteredNotes = [...this.notes];
      if (this.filterParameters) {
        this.applyFilterParameters();
      }
      return;
    }

    this.filteredNotes = this.filteredNotes.filter(
      (note) =>
        (note.title ?? '').toLowerCase().includes(searchText) ||
        (note.noteContent ?? '').toLowerCase().includes(searchText)
    );
  }

  resetFilter() {
    this.filterParameters = null;
    this.onSearch();
  }

  returnToPreviousPage() {
    this.searchText = '';
    this.filteredNotes = [...this.notes];
    this.filterParameters = null;
    this.back.emit();
  }
}
